import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { DepositDetailSearch, DepositsReportFilters } from './deposits.schema'
import type {
  DepositCurrentBalance,
  DepositDetail,
  DepositsReport,
  SalesOrderDeposits,
} from './deposits.types'

export interface DepositOrganizationScope {
  organizationId: OrganizationId
}

/** The key entries each fetcher reads; the scoped key builders in deposits.query set them. */
export interface DepositsReportQueryKeyEntries extends DepositOrganizationScope {
  filters?: DepositsReportFilters | undefined
}

export interface DepositDetailQueryKeyEntries extends DepositOrganizationScope {
  depositId?: number
  context?: DepositDetailSearch | undefined
}

export interface SalesOrderDepositsQueryKeyEntries extends DepositOrganizationScope {
  salesOrderId?: number
}

/** Fetchers read only the query key. */
type DepositQueryContext<TEntries> = Pick<QueryFunctionContext<[TEntries]>, 'queryKey'>

export interface DepositsReportExportRequest extends DepositOrganizationScope {
  filters?: DepositsReportFilters | undefined
}

export async function getDepositsReport({
  queryKey: [{ organizationId, filters }],
}: DepositQueryContext<DepositsReportQueryKeyEntries>) {
  return axios.get<DepositsReport>(
    '/reports/deposits',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}

export async function getDepositDetail({
  queryKey: [{ organizationId, depositId, context }],
}: DepositQueryContext<DepositDetailQueryKeyEntries>) {
  return axios.get<DepositDetail>(
    `/reports/deposits/${depositId}`,
    buildAdminRequestConfig({ organizationId, params: { ...context } })
  )
}

export async function getDepositCurrentBalance({
  queryKey: [{ organizationId }],
}: DepositQueryContext<DepositOrganizationScope>) {
  return axios.get<DepositCurrentBalance>(
    '/reports/deposits/current-balance',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getSalesOrderDeposits({
  queryKey: [{ organizationId, salesOrderId }],
}: DepositQueryContext<SalesOrderDepositsQueryKeyEntries>) {
  return axios.get<SalesOrderDeposits>(
    `/sales-orders/${salesOrderId}/deposits`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function exportDepositsReport({
  organizationId,
  filters,
}: DepositsReportExportRequest) {
  return rawAxios.get<Blob>(
    '/reports/deposits/export',
    buildAdminRequestConfig({
      organizationId,
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      params: { ...filters },
      responseType: 'blob',
    })
  )
}
